import React, { useCallback, useEffect } from 'react';
import useSound from 'use-sound';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import Load from '../../newComponents/Load';

import { Blob, Border } from './styles';
import { useMonitor } from '../../hooks/newMonitor';
import { useAuth } from '../../hooks/auth';

import alerta from '../../assets/alerta.wav';
import OrdersService from '../../services/OrdersService';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
    },
    nested: {
      padding: '3px 24px 3px 48px',
      color: '#7C8DB5',
      marginBottom: '8px',
    },
    nestedActive: {
      color: '#329BFC',
      borderRight: '3px solid #329BFC',
    },
    listIcon: {
      minWidth: '30px',
    },
    icon: {
      color: '#7C8DB5',
      width: '20px',
      height: '20px',
      fontSize: '20px',
    },
    iconActive: {
      color: '#329BFC',
    },
    listItem: {
      position: 'relative',
      padding: '3px 24px',
      color: '#7C8DB5',
      '& + div': {
        marginTop: '8px',
      },
    },
    withoutBorder: {
      border: 'none !important',
    },
    listItemActive: {
      color: '#329BFC',
      borderRight: '3px solid #329BFC',
    },
    listItemText: {
      '& > span': {
        fontSize: '15px',
        fontFamily: 'Sora, sans-serif',
      },
    },
    divider: {
      backgroundColor: 'rgba(124, 141, 181, 0.25)',
      margin: '16px 24px',
    },
  }),
);

const MenuListItems: React.FC = () => {
  const [playAlarm, { stop: stopAlarm }] = useSound(alerta, { volume: 1 });

  const [subMenuOpen, setSubMenuOpen] = React.useState(false);
  const [openSales, setOpenSales] = React.useState(false);
  const [openFiscal, setOpenFiscal] = React.useState(false);

  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const { caixa, user, rotinas, findRotinas } = useAuth();
  const { setEmAguardo, emAguardo } = useMonitor();

  useEffect(() => {
    async function pedidoEstagioRequest(): Promise<void> {
      if (caixa.id !== undefined) {
        OrdersService.listOrders(user.empresa_id, caixa.id, 1, 1, '0').then(
          (res) => {
            if (res.totalItems > 0) {
              setEmAguardo(true);
            }
          },
        );
      }
    }

    pedidoEstagioRequest();
  }, [caixa, setEmAguardo, user]);

  useEffect(() => {
    stopAlarm();

    if (emAguardo) {
      playAlarm();
    }

    const audioPlaying = setInterval(() => {
      if (emAguardo) {
        playAlarm();
      } else {
        stopAlarm();
      }
    }, 10000);

    return () => {
      clearInterval(audioPlaying);
    };
  }, [emAguardo, playAlarm, stopAlarm]);

  const handleClick = useCallback(() => {
    history.push('/products');
    setSubMenuOpen(!subMenuOpen);
  }, [subMenuOpen, history]);

  return (
    <div style={{ position: 'relative' }}>
      {rotinas ? (
        <>
          {findRotinas('painel_controle')?.listar && (
            <ListItem
              button
              onClick={() => history.push('/dashboard')}
              className={clsx(
                classes.listItem,
                location.pathname.includes('/dashboard') &&
                  classes.listItemActive,
              )}
            >
              <ListItemIcon className={classes.listIcon}>
                <span
                  className={clsx(
                    classes.icon,
                    'material-icons-outlined',
                    location.pathname.includes('/dashboard') &&
                      classes.iconActive,
                  )}
                >
                  dashboard
                </span>
              </ListItemIcon>
              <ListItemText
                primary="Painel de controle"
                className={classes.listItemText}
              />
            </ListItem>
          )}

          {findRotinas('painel_controle_detalhes')?.listar && (
            <ListItem
              button
              aria-current={
                location.pathname === '/inteligencia-comercial'
                  ? 'page'
                  : undefined
              }
              onClick={() => history.push('/inteligencia-comercial')}
              className={clsx(
                classes.listItem,
                location.pathname === '/inteligencia-comercial' &&
                  classes.listItemActive,
              )}
            >
              <ListItemIcon className={classes.listIcon}>
                <span
                  className={clsx(
                    classes.icon,
                    'material-icons-outlined',
                    location.pathname === '/inteligencia-comercial' &&
                      classes.iconActive,
                  )}
                >
                  analytics
                </span>
              </ListItemIcon>
              <ListItemText
                primary="Inteligência Comercial"
                className={classes.listItemText}
              />
            </ListItem>
          )}

          {findRotinas('configuracoes')?.listar && (
            <ListItem
              button
              onClick={() => history.push('/configuracoes/0')}
              className={clsx(
                classes.listItem,
                location.pathname.includes('/configuracoes') &&
                  classes.listItemActive,
              )}
            >
              <ListItemIcon className={classes.listIcon}>
                <span
                  className={clsx(
                    classes.icon,
                    'material-icons-outlined',
                    location.pathname.includes('/configuracoes') &&
                      classes.iconActive,
                  )}
                >
                  settings
                </span>
              </ListItemIcon>
              <ListItemText
                primary="Configurações"
                className={classes.listItemText}
              />
            </ListItem>
          )}

          {/* Submenu Fiscal — agrupa as telas fiscais que antes ficavam soltas
              na raiz do menu (Grupos, Regras, Categorias, Terminais, Notas,
              Saúde, Inutilização, Histórico do Contador). */}
          <ListItem
            button
            onClick={() => setOpenFiscal(!openFiscal)}
            className={classes.listItem}
          >
            <ListItemIcon className={classes.listIcon}>
              <span className={clsx(classes.icon, 'material-icons-outlined')}>
                request_quote
              </span>
            </ListItemIcon>
            <ListItemText primary="Fiscal" className={classes.listItemText} />
            {openFiscal ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </ListItem>

          <Collapse in={openFiscal} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                onClick={() => history.push('/grupos-tributarios')}
                className={clsx(
                  classes.nested,
                  location.pathname.includes('/grupos-tributarios') &&
                    classes.listItemActive,
                )}
              >
                <ListItemIcon className={classes.listIcon}>
                  <span
                    className={clsx(
                      classes.icon,
                      'material-icons-outlined',
                      location.pathname.includes('/grupos-tributarios') &&
                        classes.iconActive,
                    )}
                  >
                    account_balance
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary="Grupos Tributários"
                  className={classes.listItemText}
                />
              </ListItem>

              <ListItem
                button
                onClick={() => history.push('/regras-ibs-cbs-is')}
                className={clsx(
                  classes.nested,
                  location.pathname.includes('/regras-ibs-cbs-is') &&
                    classes.listItemActive,
                )}
              >
                <ListItemIcon className={classes.listIcon}>
                  <span
                    className={clsx(
                      classes.icon,
                      'material-icons-outlined',
                      location.pathname.includes('/regras-ibs-cbs-is') &&
                        classes.iconActive,
                    )}
                  >
                    gavel
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary="Regras IBS/CBS/IS"
                  className={classes.listItemText}
                />
              </ListItem>

              <ListItem
                button
                onClick={() => history.push('/categorias-fiscais')}
                className={clsx(
                  classes.nested,
                  location.pathname.includes('/categorias-fiscais') &&
                    classes.listItemActive,
                )}
              >
                <ListItemIcon className={classes.listIcon}>
                  <span
                    className={clsx(
                      classes.icon,
                      'material-icons-outlined',
                      location.pathname.includes('/categorias-fiscais') &&
                        classes.iconActive,
                    )}
                  >
                    category
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary="Categorias Fiscais"
                  className={classes.listItemText}
                />
              </ListItem>

              <ListItem
                button
                onClick={() => history.push('/terminais-fiscais')}
                className={clsx(
                  classes.nested,
                  location.pathname.includes('/terminais-fiscais') &&
                    classes.listItemActive,
                )}
              >
                <ListItemIcon className={classes.listIcon}>
                  <span
                    className={clsx(
                      classes.icon,
                      'material-icons-outlined',
                      location.pathname.includes('/terminais-fiscais') &&
                        classes.iconActive,
                    )}
                  >
                    point_of_sale
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary="Terminais"
                  className={classes.listItemText}
                />
              </ListItem>

              <ListItem
                button
                onClick={() => history.push('/notas-emitidas')}
                className={clsx(
                  classes.nested,
                  location.pathname.includes('/notas-emitidas') &&
                    classes.listItemActive,
                )}
              >
                <ListItemIcon className={classes.listIcon}>
                  <span
                    className={clsx(
                      classes.icon,
                      'material-icons-outlined',
                      location.pathname.includes('/notas-emitidas') &&
                        classes.iconActive,
                    )}
                  >
                    receipt
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary="Notas Emitidas"
                  className={classes.listItemText}
                />
              </ListItem>

              <ListItem
                button
                onClick={() => history.push('/saude-fiscal')}
                className={clsx(
                  classes.nested,
                  location.pathname.includes('/saude-fiscal') &&
                    classes.listItemActive,
                )}
              >
                <ListItemIcon className={classes.listIcon}>
                  <span
                    className={clsx(
                      classes.icon,
                      'material-icons-outlined',
                      location.pathname.includes('/saude-fiscal') &&
                        classes.iconActive,
                    )}
                  >
                    health_and_safety
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary="Saúde Fiscal"
                  className={classes.listItemText}
                />
              </ListItem>

              <ListItem
                button
                onClick={() => history.push('/inutilizacao')}
                className={clsx(
                  classes.nested,
                  location.pathname.includes('/inutilizacao') &&
                    classes.listItemActive,
                )}
              >
                <ListItemIcon className={classes.listIcon}>
                  <span
                    className={clsx(
                      classes.icon,
                      'material-icons-outlined',
                      location.pathname.includes('/inutilizacao') &&
                        classes.iconActive,
                    )}
                  >
                    block
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary="Inutilização"
                  className={classes.listItemText}
                />
              </ListItem>

              <ListItem
                button
                onClick={() => history.push('/historico-contador')}
                className={clsx(
                  classes.nested,
                  location.pathname.includes('/historico-contador') &&
                    classes.listItemActive,
                )}
              >
                <ListItemIcon className={classes.listIcon}>
                  <span
                    className={clsx(
                      classes.icon,
                      'material-icons-outlined',
                      location.pathname.includes('/historico-contador') &&
                        classes.iconActive,
                    )}
                  >
                    folder_zip
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary="Histórico do Contador"
                  className={classes.listItemText}
                />
              </ListItem>
            </List>
          </Collapse>

          {findRotinas('horarios')?.listar && (
            <ListItem
              button
              onClick={() => history.push('/schedules')}
              className={clsx(
                classes.listItem,
                location.pathname.includes('/schedules') &&
                  classes.listItemActive,
              )}
            >
              <ListItemIcon className={classes.listIcon}>
                <span
                  className={clsx(
                    classes.icon,
                    'material-icons-outlined',
                    location.pathname.includes('/schedules') &&
                      classes.iconActive,
                  )}
                >
                  schedule
                </span>
              </ListItemIcon>
              <ListItemText
                primary="Horários"
                className={classes.listItemText}
              />
            </ListItem>
          )}

          {findRotinas('promocoes')?.listar && (
            <ListItem
              button
              onClick={() => setOpenSales(!openSales)}
              className={classes.listItem}
            >
              <ListItemIcon className={classes.listIcon}>
                <span className={clsx(classes.icon, 'material-icons-outlined')}>
                  sell
                </span>
              </ListItemIcon>
              <ListItemText
                primary="Promoções"
                className={classes.listItemText}
              />
              {openSales ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </ListItem>
          )}

          <Collapse in={openSales} timeout="auto" unmountOnExit>
            {findRotinas('cupom_de_desconto')?.listar && (
              <List component="div" disablePadding>
                <ListItem
                  button
                  onClick={() => history.push('/cupom-desconto')}
                  className={clsx(
                    classes.nested,
                    location.pathname.includes('/cupom-desconto') &&
                      classes.listItemActive,
                  )}
                >
                  <ListItemIcon className={classes.listIcon}>
                    <span
                      className={clsx(
                        classes.icon,
                        'material-icons-outlined',
                        location.pathname.includes('/cupom-desconto') &&
                          classes.iconActive,
                      )}
                    >
                      confirmation_number
                    </span>
                  </ListItemIcon>
                  <ListItemText
                    primary="Cupom de desconto"
                    className={classes.listItemText}
                  />
                </ListItem>

                <ListItem
                  button
                  onClick={() => history.push('/frete-gratis')}
                  className={clsx(
                    classes.nested,
                    location.pathname.includes('/frete-gratis') &&
                      classes.listItemActive,
                  )}
                >
                  <ListItemIcon className={classes.listIcon}>
                    <span
                      className={clsx(
                        classes.icon,
                        'material-icons-outlined',
                        location.pathname.includes('/frete-gratis') &&
                          classes.iconActive,
                      )}
                    >
                      motorcycle
                    </span>
                  </ListItemIcon>
                  <ListItemText
                    primary="Frete grátis"
                    className={classes.listItemText}
                  />
                </ListItem>

                {/* <ListItem
                  button
                  onClick={() => history.push('/primeira-compra')}
                  className={clsx(
                    classes.nested,
                    location.pathname.includes('/primeira-compra') &&
                      classes.listItemActive,
                  )}
                >
                  <ListItemIcon className={classes.listIcon}>
                    <span
                      className={clsx(
                        classes.icon,
                        'material-icons-outlined',
                        location.pathname.includes('/primeira-compra') &&
                          classes.iconActive,
                      )}
                    >
                      money
                    </span>
                  </ListItemIcon>
                  <ListItemText
                    primary="Primeira compra"
                    className={classes.listItemText}
                  />
                </ListItem> */}
              </List>
            )}
          </Collapse>

          <ListItem
            button
            onClick={() => history.push('/areas-restritas')}
            className={clsx(
              classes.listItem,
              location.pathname.includes('/areas-restritas') &&
                classes.listItemActive,
            )}
          >
            <ListItemIcon className={classes.listIcon}>
              <span
                className={clsx(
                  classes.icon,
                  'material-icons-outlined',
                  location.pathname.includes('/areas-restritas') &&
                    classes.iconActive,
                )}
              >
                wrong_location
              </span>
            </ListItemIcon>
            <ListItemText
              primary="Áreas restritas"
              className={classes.listItemText}
            />
          </ListItem>

          <Divider className={classes.divider} />

          {findRotinas('monitor_de_pedidos')?.listar && (
            <ListItem
              button
              onClick={() => history.push('/monitor_pedidos')}
              className={clsx(
                classes.listItem,
                location.pathname.includes('/monitor_pedidos') &&
                  classes.listItemActive,
                emAguardo && classes.withoutBorder,
              )}
            >
              <ListItemIcon className={classes.listIcon}>
                <span
                  className={clsx(
                    classes.icon,
                    'material-icons-outlined',
                    location.pathname.includes('/monitor_pedidos') &&
                      classes.iconActive,
                  )}
                >
                  receipt_long
                </span>
              </ListItemIcon>
              <ListItemText
                primary="Monitor de pedidos"
                className={classes.listItemText}
              />
              {emAguardo && (
                <>
                  <Blob />
                  <Border />
                </>
              )}
            </ListItem>
          )}

          {findRotinas('produtos')?.listar && (
            <ListItem
              button
              onClick={handleClick}
              className={clsx(
                classes.listItem,
                location.pathname.includes('/products') &&
                  classes.listItemActive,
              )}
            >
              <ListItemIcon className={classes.listIcon}>
                <span
                  className={clsx(
                    classes.icon,
                    'material-icons-outlined',
                    location.pathname.includes('/products') &&
                      classes.iconActive,
                  )}
                >
                  inventory_2
                </span>
              </ListItemIcon>
              <ListItemText
                primary="Produtos"
                className={classes.listItemText}
              />
              {subMenuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </ListItem>
          )}

          <Collapse in={subMenuOpen} timeout="auto" unmountOnExit>
            {findRotinas('materia_prima')?.listar && (
              <List component="div" disablePadding>
                <ListItem
                  button
                  className={clsx(
                    classes.nested,
                    location.pathname.includes('/produtos/materia_prima') &&
                      classes.listItemActive,
                  )}
                  onClick={() => history.push('/produtos/materia_prima')}
                >
                  <ListItemIcon className={classes.listIcon}>
                    <span
                      className={clsx(
                        classes.icon,
                        'material-icons-outlined',
                        location.pathname.includes('/produtos/materia_prima') &&
                          classes.iconActive,
                      )}
                    >
                      liquor
                    </span>
                  </ListItemIcon>
                  <ListItemText
                    primary="Matéria prima"
                    className={classes.listItemText}
                  />
                </ListItem>
              </List>
            )}
          </Collapse>

          {findRotinas('historico')?.listar && (
            <ListItem
              button
              onClick={() => history.push('/historico')}
              className={clsx(
                classes.listItem,
                location.pathname.includes('/historico') &&
                  classes.listItemActive,
              )}
            >
              <ListItemIcon className={classes.listIcon}>
                <span
                  className={clsx(
                    classes.icon,
                    'material-icons-outlined',
                    location.pathname.includes('/historico') &&
                      classes.iconActive,
                  )}
                >
                  history
                </span>
              </ListItemIcon>
              <ListItemText
                primary="Histórico"
                className={classes.listItemText}
              />
            </ListItem>
          )}

          <ListItem
            button
            onClick={() => history.push('/etiquetas')}
            className={clsx(
              classes.listItem,
              location.pathname.includes('/etiquetas') &&
                classes.listItemActive,
            )}
          >
            <ListItemIcon className={classes.listIcon}>
              <span
                className={clsx(
                  classes.icon,
                  'material-icons-outlined',
                  location.pathname.includes('/etiquetas') &&
                    classes.iconActive,
                )}
              >
                label
              </span>
            </ListItemIcon>
            <ListItemText
              primary="Etiquetas"
              className={classes.listItemText}
            />
          </ListItem>

          <Divider className={classes.divider} />

          <ListItem
            button
            onClick={() => history.push('/privacy-policy')}
            className={clsx(
              classes.listItem,
              location.pathname.includes('/privacy-policy') &&
                classes.listItemActive,
            )}
          >
            <ListItemIcon className={classes.listIcon}>
              <span
                className={clsx(
                  classes.icon,
                  'material-icons-outlined',
                  location.pathname.includes('/privacy-policy') &&
                    classes.iconActive,
                )}
              >
                article
              </span>
            </ListItemIcon>
            <ListItemText
              primary="Políticas do sistema"
              className={classes.listItemText}
            />
          </ListItem>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Load />
        </div>
      )}
    </div>
  );
};

export default MenuListItems;
